import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Search, Check } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import DateField from "@/components/masterOrder/DateField";
import CurrencyField from "@/components/masterOrder/CurrencyField";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  listClients, createClient, listProductTypes, createProductType,
  createOrder, createOrderItem, getSettings, DELIVERY_TYPES,
  createOrderPayment, displayOrderNumber,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ------------------------------ Client Picker ---------------------------- */
function ClientPicker({ clients, value, onChange, onCreate }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients.slice(0, 8);
    return clients.filter((c) =>
      c.name.toLowerCase().includes(s) || (c.phone || "").toLowerCase().includes(s)
    ).slice(0, 12);
  }, [q, clients]);

  return (
    <div className="relative">
      {selected && !open ? (
        <div className="flex items-center justify-between h-11 px-3 rounded-lg bg-white"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-new-order-client-selected">
          <div>
            <div className="text-[13px]" style={{ color: "#2A1D14" }}>{selected.name}</div>
            <div className="text-[11px]" style={{ color: "#8B6F4B" }}>{selected.phone}</div>
          </div>
          <button onClick={() => { onChange(null); setOpen(true); }} className="text-[12px] px-3 h-8 rounded-full"
                  style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728" }}
                  data-testid="mo-new-order-client-change">
            Сменить
          </button>
        </div>
      ) : (
        <>
          <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                 placeholder="Начните вводить имя или телефон..."
                 className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                 style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-new-order-client-search" />
          {open && (
            <div className="absolute z-30 mt-1 w-full rounded-xl bg-white overflow-hidden"
                 style={{ boxShadow: "0 12px 28px -12px rgba(58,43,31,0.24), inset 0 0 0 1px #EFE4D0" }}>
              <div className="max-h-56 overflow-y-auto">
                {filtered.map((c) => (
                  <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                          className="w-full text-left px-3 py-2 text-[13px] hover:bg-black/5"
                          data-testid={`mo-new-order-client-opt-${c.id}`}>
                    <div style={{ color: "#2A1D14" }}>{c.name}</div>
                    <div className="text-[11px]" style={{ color: "#8B6F4B" }}>{c.phone}</div>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <div className="px-3 py-3 text-[12px]" style={{ color: "#8B6F4B" }}>Ничего не найдено.</div>
                )}
              </div>
              <button type="button" onClick={onCreate}
                      className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] border-t"
                      style={{ borderColor: "#EFE4D0", color: "#4A3728", background: "#FBF5EA" }}
                      data-testid="mo-new-order-client-create">
                <Plus size={13} strokeWidth={1.8} /> Быстро добавить клиента
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* --------------------------- Quick create client ------------------------- */
function QuickClientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", messenger: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", phone: "", messenger: "" }); }, [open]);
  if (!open) return null;
  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Имя и телефон обязательны");
    setSaving(true);
    try {
      const c = await createClient({
        name: form.name.trim(), phone: form.phone.trim(),
        messenger: form.messenger.trim() || null,
      });
      toast.success("Клиент добавлен");
      onCreated(c);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white overflow-hidden" onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "0 30px 60px -20px rgba(35,23,16,0.4)" }}>
        <div className="px-5 py-4 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Быстрое добавление клиента</div>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Имя *"><Input value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="mo-quick-client-name" /></Field>
          <Field label="Телефон *"><Input value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} testid="mo-quick-client-phone" placeholder="+..." /></Field>
          <Field label="Мессенджер"><Input value={form.messenger} onChange={(v) => setForm(f => ({ ...f, messenger: v }))} /></Field>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "#EFE4D0", background: "#FBF5EA" }}>
          <button onClick={onClose} className="h-10 px-4 rounded-full text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>Отменить</button>
          <button onClick={save} disabled={saving} className="h-10 px-5 rounded-full text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-quick-client-save">
            {saving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Item row --------------------------------- */
function ProductTypeCombobox({ value, productTypes, onSelect, onCreate, testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const selected = productTypes.find((p) => p.id === value);
  const s = q.trim();
  const filtered = useMemo(() => {
    const ls = s.toLowerCase();
    return productTypes
      .filter((p) => p.is_active !== false)
      .filter((p) => !ls || (p.name || "").toLowerCase().includes(ls))
      .slice(0, 20);
  }, [productTypes, s]);
  const exact = productTypes.find((p) => (p.name || "").toLowerCase() === s.toLowerCase());
  const showCreate = s.length > 0 && !exact;

  const handleCreate = async () => {
    if (!s) return;
    setCreating(true);
    try {
      const pt = await onCreate(s);
      if (pt) { onSelect(pt.id); setQ(""); setOpen(false); }
    } finally { setCreating(false); }
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full h-10 px-3 rounded-lg text-[13px] text-left flex items-center justify-between gap-2 bg-white"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: selected ? "#2A1D14" : "#8B6F4B" }}
              data-testid={testid}>
        <span className="truncate">{selected ? selected.name : "— выбрать или создать —"}</span>
        <Search size={13} strokeWidth={1.7} className="shrink-0" color="#8B6F4B" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg bg-white overflow-hidden"
               style={{ boxShadow: "0 12px 30px -12px rgba(35,23,16,0.35), inset 0 0 0 1px #EFE4D0" }}>
            <div className="p-2 border-b" style={{ borderColor: "#EFE4D0" }}>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" color="#8B6F4B" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter" && showCreate) { e.preventDefault(); handleCreate(); } }}
                       placeholder="Торт, бенто, пряничный набор..."
                       className="w-full h-9 pl-7 pr-2 rounded-md text-[12.5px] outline-none bg-[#FBF5EA]"
                       style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                       data-testid={`${testid}-search`} />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.length === 0 && !showCreate && (
                <div className="px-3 py-3 text-[12px]" style={{ color: "#8B6F4B" }}>
                  Начните вводить название типа продукта.
                </div>
              )}
              {filtered.map((p) => (
                <button key={p.id} type="button"
                        onClick={() => { onSelect(p.id); setOpen(false); setQ(""); }}
                        className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-[#FBF5EA] flex items-center gap-2 ${p.id === value ? "bg-[#F5EBD8]" : ""}`}
                        style={{ color: "#2A1D14" }}
                        data-testid={`${testid}-option-${p.id}`}>
                  {p.id === value && <Check size={12} color="#3E5236" />}
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
              {showCreate && (
                <button type="button" onClick={handleCreate} disabled={creating}
                        className="w-full text-left px-3 py-2 text-[13px] border-t bg-[#F5EBD8]/60 hover:bg-[#F5EBD8] flex items-center gap-2 disabled:opacity-60"
                        style={{ borderColor: "#EFE4D0", color: "#2A1D14" }}
                        data-testid={`${testid}-create`}>
                  <Plus size={12} color="#4A3728" />
                  <span>{creating ? "Создаём…" : <>Создать «<b>{s}</b>»</>}</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OrderItemRow({ item, productTypes, onChange, onRemove, onCreateProductType, index }) {
  return (
    <div className="rounded-xl p-3 bg-white" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={`mo-new-order-item-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Позиция #{index + 1}</div>
        <button type="button" onClick={onRemove}
                className="w-8 h-8 rounded-full grid place-items-center hover:bg-red-50"
                style={{ color: "#8A3F1E" }} aria-label="Удалить позицию"
                data-testid={`mo-new-order-item-remove-${index}`}>
          <Trash2 size={13} strokeWidth={1.7} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Тип продукта</label>
          <div className="mt-1">
            <ProductTypeCombobox
              value={item.product_type_id || ""}
              productTypes={productTypes}
              onSelect={(id) => onChange({ ...item, product_type_id: id })}
              onCreate={onCreateProductType}
              testid={`mo-new-order-item-type-${index}`}
            />
          </div>
        </div>
        <Field label="Количество">
          <Input value={String(item.quantity ?? 1)} onChange={(v) => onChange({ ...item, quantity: parseInt(v || "0", 10) || 0 })}
                 testid={`mo-new-order-item-qty-${index}`} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Состав / вкус">
            <Input value={item.flavor_composition || ""} onChange={(v) => onChange({ ...item, flavor_composition: v })}
                   placeholder="Шоколад, фундук, солёная карамель" testid={`mo-new-order-item-flavor-${index}`} />
          </Field>
        </div>
        <Field label="Размер (текстом)">
          <Input value={item.size_display || ""} onChange={(v) => onChange({ ...item, size_display: v })}
                 placeholder="20 см, 3 яруса" testid={`mo-new-order-item-size-${index}`} />
        </Field>
        <div className="md:col-span-3">
          <Field label="Пожелания / заметка">
            <textarea value={item.notes || ""} onChange={(e) => onChange({ ...item, notes: e.target.value })}
                      className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-none"
                      style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid={`mo-new-order-item-notes-${index}`} />
          </Field>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------- Page --------------------------------- */
export default function MOOrderNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialClientId = sp.get("clientId") || null;

  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("EUR");

  const [quickClientOpen, setQuickClientOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);
  const todayDate = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const [form, setForm] = useState({
    client_id: initialClientId,
    event_type: "",
    event_date: null,
    event_time: "",
    guests_count: "",
    wishes: "",
    dislikes: "",
    allergies: "",
    design_notes: "",
    budget: "",
    currency_code: "EUR",
    delivery_type: "",
    delivery_address: "",
    delivery_date: null,
    delivery_time_window: "",
    delivery_cost: "",
    delivery_notes: "",
    recipient_name: "",
    recipient_phone: "",
    recipient_notes: "",
    // Sprint MO-UX-Simplify: optional prepayment captured at order creation.
    prepay_amount: "",
    prepay_method: "cash",
  });
  const [items, setItems] = useState([{ product_type_id: "", quantity: 1 }]);

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      setLoading(true);
      try {
        const [cls, pts, s] = await Promise.all([listClients(), listProductTypes(), getSettings()]);
        setClients(cls);
        setProductTypes(pts);
        const cur = s?.default_currency_code || "EUR";
        setDefaultCurrency(cur);
        setForm((f) => ({ ...f, currency_code: cur }));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [aLoad, mode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedClient = clients.find((c) => c.id === form.client_id) || null;

  const submit = async () => {
    if (!form.client_id) return toast.error("Выберите клиента");
    if (!form.event_date) return toast.error("Укажите дату события");
    if (!form.currency_code || form.currency_code.length !== 3) return toast.error("Укажите валюту заказа (ISO)");
    const validItems = items.filter((it) => it.product_type_id && (it.quantity || 0) > 0);
    if (validItems.length === 0) return toast.error("Добавьте хотя бы одну позицию");

    setSaving(true);
    try {
      const orderPayload = {
        client_id: form.client_id,
        event_type: form.event_type || null,
        event_date: form.event_date,
        event_time: form.event_time || null,
        guests_count: form.guests_count ? parseInt(form.guests_count, 10) : null,
        wishes: form.wishes || null,
        dislikes: form.dislikes || null,
        allergies: form.allergies || null,
        design_notes: form.design_notes || null,
        budget: form.budget || null,
        currency_code: form.currency_code,
      };
      if (form.delivery_type) {
        orderPayload.delivery = {
          type: form.delivery_type,
          address: form.delivery_address || null,
          date: form.delivery_date || null,
          time_window: form.delivery_time_window || null,
          cost: form.delivery_cost || null,
          notes: form.delivery_notes || null,
        };
      }
      if (form.recipient_name || form.recipient_phone || form.recipient_notes) {
        orderPayload.recipient = {
          name: form.recipient_name || null,
          phone: form.recipient_phone || null,
          notes: form.recipient_notes || null,
        };
      }

      const order = await createOrder(orderPayload);
      // Create items sequentially (each POST returns its own record).
      for (const it of validItems) {
        await createOrderItem({
          order_id: order.id,
          product_type_id: it.product_type_id,
          quantity: it.quantity,
          flavor_composition: it.flavor_composition || null,
          size_display: it.size_display || null,
          notes: it.notes || null,
        });
      }
      toast.success(`Заказ № ${String(order.order_number || "").replace(/^0+/, "") || "0"} создан`);
      // Sprint MO-UX-Simplify: optional prepayment captured at create.
      const prepayNum = parseFloat(String(form.prepay_amount || "").replace(",", "."));
      if (Number.isFinite(prepayNum) && prepayNum > 0) {
        try {
          await createOrderPayment({
            order_id: order.id,
            amount_minor: Math.round(prepayNum * 100),
            currency_code: form.currency_code,
            method: form.prepay_method || "cash",
            type: "prepayment",
            paid_at: new Date().toISOString(),
          });
        } catch (e) {
          toast.error("Заказ создан, но не удалось внести предоплату: " + formatApiError(e));
        }
      }
      nav(`/master-order/orders/${order.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const createProductTypeInline = async (name) => {
    const nm = String(name || "").trim();
    if (!nm) return null;
    try {
      const pt = await createProductType({ name: nm, is_active: true });
      setProductTypes((v) => [...v, pt]);
      toast.success(`Тип «${pt.name}» добавлен`);
      return pt;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  return (
    <MOShell active="orders" title="Новый заказ" subtitle="Создание черновика">
      {aLoad ? <LoadingBlock /> : mode === "no_access" || mode === "demo" ? <NoAccessScreen /> :
       !canWrite ? (
         <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
           <div className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
             Режим просмотра — создание заказов недоступно.
           </div>
         </div>
       ) : loading ? <LoadingBlock /> : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => nav("/master-order/orders")}
                    className="text-[12.5px] inline-flex items-center gap-1.5"
                    style={{ color: "#8B6F4B" }}
                    data-testid="mo-new-order-back">
              <ArrowLeft size={13} strokeWidth={1.8} /> К списку заказов
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4">
            {/* Left: main data */}
            <div className="space-y-4">
              {/* Client */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Клиент
                </div>
                <ClientPicker clients={clients} value={form.client_id}
                              onChange={(id) => set("client_id", id)}
                              onCreate={() => setQuickClientOpen(true)} />
              </section>

              {/* Event */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Событие
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Дата события *">
                    <DateField value={form.event_date} onChange={(v) => set("event_date", v)}
                               minDate={todayDate} testid="mo-new-order-event-date" />
                  </Field>
                  <Field label="Время (по желанию)">
                    <input type="time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)}
                           className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                           style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-new-order-event-time" />
                  </Field>
                  <Field label="Тип события">
                    <Input value={form.event_type} onChange={(v) => set("event_type", v)}
                           placeholder="Свадьба, день рождения..." testid="mo-new-order-event-type" />
                  </Field>
                  <Field label="Гостей (по желанию)">
                    <Input value={form.guests_count} onChange={(v) => set("guests_count", v.replace(/\D/g, ""))}
                           testid="mo-new-order-guests" />
                  </Field>
                  <Field label="Валюта заказа *">
                    <CurrencyField value={form.currency_code} onChange={(c) => set("currency_code", c)}
                                   testid="mo-new-order-currency" />
                  </Field>
                  <Field label="Клиентский бюджет">
                    <Input value={form.budget} onChange={(v) => set("budget", v.replace(/[^\d.,]/g, ""))}
                           placeholder="0.00" testid="mo-new-order-budget" />
                  </Field>
                </div>
              </section>

              {/* Items */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Позиции заказа
                  </div>
                  <button type="button" onClick={() => setItems((it) => [...it, { product_type_id: "", quantity: 1 }])}
                          className="h-9 px-3 rounded-full text-[12.5px] inline-flex items-center gap-1.5"
                          style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                          data-testid="mo-new-order-add-item">
                    <Plus size={13} strokeWidth={1.8} /> Ещё позиция
                  </button>
                </div>
                <div className="space-y-3">
                  {items.map((it, idx) => (
                    <OrderItemRow key={idx} item={it} index={idx} productTypes={productTypes}
                                  onChange={(v) => setItems((cur) => cur.map((x, i) => i === idx ? v : x))}
                                  onCreateProductType={createProductTypeInline}
                                  onRemove={() => setItems((cur) => cur.filter((_, i) => i !== idx))} />
                  ))}
                </div>
                {productTypes.length === 0 && (
                  <div className="mt-3 p-3 rounded-lg text-[12px]"
                       style={{ background: "#FBF5EA", color: "#5B4326", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                    Ещё нет типов продукта — начните вводить название в поле «Тип продукта» и создайте его прямо из поиска.
                  </div>
                )}
              </section>

              {/* Wishes + client allergies + this-order restrictions */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Пожелания и ограничения
                </div>
                <div className="space-y-3">
                  <Field label="Пожелания / дизайн">
                    <textarea value={form.wishes} onChange={(e) => set("wishes", e.target.value)}
                              placeholder="Оформление, надписи, декор, референсы, живые цветы, топпер, съёмный декор — важные детали именно этого заказа."
                              className="w-full h-28 p-3 rounded-lg text-[13px] outline-none resize-y"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              data-testid="mo-new-order-wishes" />
                  </Field>
                  {selectedClient?.allergies_common && (
                    <div className="rounded-lg px-3 py-2"
                         style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                         data-testid="mo-new-order-client-allergies">
                      <div className="text-[10.5px] tracking-[0.22em] uppercase mb-0.5" style={{ color: "#8B6F4B" }}>
                        Из карточки клиента
                      </div>
                      <div className="text-[12.5px]" style={{ color: "#5B4326" }}>
                        {selectedClient.allergies_common}
                      </div>
                    </div>
                  )}
                  <Field label="Дополнительные ограничения для этого заказа">
                    <textarea value={form.allergies} onChange={(e) => set("allergies", e.target.value)}
                              placeholder="Только новые ограничения именно этого заказа."
                              className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-y"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              data-testid="mo-new-order-restrictions" />
                  </Field>
                </div>
              </section>
            </div>

            {/* Right: delivery + recipient + submit */}
            <div className="space-y-4">
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Получение
                </div>
                <div className="space-y-3">
                  <Field label="Способ">
                    <select value={form.delivery_type} onChange={(e) => set("delivery_type", e.target.value)}
                            className="w-full h-10 px-3 rounded-lg text-[13px] outline-none bg-white"
                            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                            data-testid="mo-new-order-delivery-type">
                      <option value="">Не выбрано</option>
                      {DELIVERY_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </Field>
                  {form.delivery_type && (
                    <>
                      <Field label="Адрес">
                        <Input value={form.delivery_address} onChange={(v) => set("delivery_address", v)}
                               placeholder="Город, улица, дом..." testid="mo-new-order-delivery-address" />
                      </Field>
                      <Field label="Дата доставки">
                        <DateField value={form.delivery_date} onChange={(v) => set("delivery_date", v)}
                                   testid="mo-new-order-delivery-date" />
                      </Field>
                      <Field label="Временное окно">
                        <Input value={form.delivery_time_window} onChange={(v) => set("delivery_time_window", v)}
                               placeholder="11:00 - 13:00" />
                      </Field>
                      <Field label="Стоимость доставки">
                        <Input value={form.delivery_cost} onChange={(v) => set("delivery_cost", v.replace(/[^\d.,]/g, ""))}
                               placeholder="0.00" />
                      </Field>
                      <Field label="Заметка">
                        <textarea value={form.delivery_notes} onChange={(e) => set("delivery_notes", e.target.value)}
                                  className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-none"
                                  style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                      </Field>
                    </>
                  )}
                </div>
              </section>

              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Получатель
                </div>
                <div className="space-y-3">
                  <Field label="Имя"><Input value={form.recipient_name} onChange={(v) => set("recipient_name", v)} testid="mo-new-order-recipient-name" /></Field>
                  <Field label="Телефон"><Input value={form.recipient_phone} onChange={(v) => set("recipient_phone", v)} placeholder="+..." /></Field>
                  <Field label="Заметка">
                    <textarea value={form.recipient_notes} onChange={(e) => set("recipient_notes", e.target.value)}
                              className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-none"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  </Field>
                </div>
              </section>

              {/* Optional prepayment (Sprint MO-UX-Simplify) */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Предоплата (необязательно)
                </div>
                <div className="text-[11.5px] mb-3" style={{ color: "#8B6F4B" }}>
                  Если клиент уже внёс деньги, укажите сумму — она появится в истории оплат.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={`Сумма, ${form.currency_code || "EUR"}`}>
                    <input value={form.prepay_amount}
                           type="text" inputMode="decimal"
                           onChange={(e) => set("prepay_amount", e.target.value.replace(/[^\d.,]/g, ""))}
                           placeholder="например, 40"
                           className="w-full h-11 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-new-order-prepay-amount" />
                  </Field>
                  <Field label="Способ оплаты">
                    <select value={form.prepay_method}
                            onChange={(e) => set("prepay_method", e.target.value)}
                            className="w-full h-11 px-3 rounded-md bg-white text-base outline-none"
                            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                            data-testid="mo-new-order-prepay-method">
                      <option value="cash">Наличные</option>
                      <option value="bank_transfer">Банковский перевод</option>
                      <option value="card">Карта</option>
                      <option value="other">Другое</option>
                    </select>
                  </Field>
                </div>
              </section>

              <div className="rounded-xl bg-white p-4 flex flex-col md:flex-row md:items-center gap-2 justify-end sticky bottom-4"
                   style={{ boxShadow: "0 8px 22px -12px rgba(58,43,31,0.35), inset 0 0 0 1px #EFE4D0" }}>
                <button onClick={() => nav("/master-order/orders")}
                        className="h-10 px-4 rounded-full text-[12.5px]"
                        style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
                  Отменить
                </button>
                <button onClick={submit} disabled={saving}
                        className="h-11 px-5 rounded-full text-[13px] inline-flex items-center gap-2 disabled:opacity-60"
                        style={{ background: "#4A3728", color: "#F7EFE0" }}
                        data-testid="mo-new-order-submit">
                  <Save size={14} strokeWidth={1.8} /> {saving ? "Создание..." : "Создать заказ"}
                </button>
              </div>
            </div>
          </div>

          <QuickClientModal open={quickClientOpen} onClose={() => setQuickClientOpen(false)}
                            onCreated={(c) => { setClients((v) => [c, ...v]); setForm((f) => ({ ...f, client_id: c.id })); setQuickClientOpen(false); }} />
        </div>
      )}
    </MOShell>
  );
}
